import React, { useEffect, useRef, useState } from "react";
import { api, wsUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Send } from "lucide-react";
import { getToken } from "@/lib/session";

export default function ChatPanel({ roomId, title }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const wsRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;
    (async () => {
      const r = await api.get(`/chat/${roomId}/messages`);
      if (!cancelled) setMessages(r.data);
    })();

    const token = getToken();
    const url = wsUrl(`/api/ws/chat/${roomId}?token=${encodeURIComponent(token || "")}`);
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "message") {
          setMessages((prev) => [...prev, data.message]);
        }
      } catch (err) {
        console.warn("Chat WS message parse failed:", err);
      }
    };
    ws.onclose = () => {};

    return () => { cancelled = true; ws.close(); };
  }, [roomId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!input.trim()) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ text: input }));
    } else {
      // fallback
      const r = await api.post(`/chat/${roomId}/messages`, { text: input });
      setMessages((prev) => [...prev, r.data]);
    }
    setInput("");
  };

  return (
    <div className="card-pb flex flex-col h-[70vh]" data-testid="chat-panel">
      <div className="px-5 py-4 border-b border-[#E2E8F0]">
        <h3 className="font-heading text-lg text-[#0A2540]">{title || "Messages"}</h3>
        <p className="text-xs text-[#475569]">Direct line to PowerBuilt team</p>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-3">
        {messages.length === 0 && (
          <p className="text-center text-sm text-[#475569] mt-10">Start the conversation</p>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === user.user_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.message_id}`}>
              <div className={`max-w-[75%] rounded-xl px-4 py-2.5 ${mine ? "bg-[#0A2540] text-white" : "bg-slate-100 text-[#0A2540]"}`}>
                {!mine && <div className="text-xs font-medium mb-0.5 text-[#C9A356]">{m.sender_name} · {m.sender_role}</div>}
                <div className="text-sm leading-relaxed">{m.text}</div>
                <div className={`text-[10px] mt-1 ${mine ? "text-slate-300" : "text-[#94A3B8]"}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="border-t border-[#E2E8F0] p-3 flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type a message..."
          data-testid="chat-input"
          className="flex-1 border border-[#E2E8F0] rounded-md px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#C9A356]/50 focus:border-[#C9A356]"
        />
        <button onClick={send} className="btn-accent px-4" data-testid="chat-send">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
